import { Injectable } from '@angular/core';
import {
  Account,
  ActiveStatus,
  ActiveStatusEnum,
  BaseEntity,
  PricedProductType,
  Product,
  ProductType,
  ProductTypeConfig,
  ProductTypeConfigProduct,
  Program,
  ProgramProductTypeConfig,
  Supplier,
} from '@models';
import { UtilDialogService } from './dialog.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Guid } from 'guid-typescript';
import { IStatus, StatusChangeDialog } from '../components';
import { DbQueryService, DbSaveService } from '@data';
import { EntityAspect } from 'breeze-client';
import { ToastrService } from 'ngx-toastr';

export enum IStatusEnum {
  Active = 1,
  Hold = 2,
  Phaseout = 3,
  Inactive = 4,
}

export type ProductRelatedEntity = ProgramProductTypeConfig | PricedProductType | ProductType | ProductTypeConfigProduct | Product;

// Lowest to highest priority
export const StatusPriority = [IStatusEnum.Active, IStatusEnum.Phaseout, IStatusEnum.Hold, IStatusEnum.Inactive];

// Implemented on all Entities with a status.
export interface IEntityWithStatus extends BaseEntity {
  getStatusId: () => 1 | 2 | 3 | 4; // IStatusEnum  - because of the way that TS treats enums ( no overlap between them)
  getStatusParents: () => IEntityWithStatus[];
  name?: string; // name of the entity
  isApiEnabled?: boolean; // if the entity is API enabled
}

export interface IStatusResult {
  baseEntity: IEntityWithStatus;
  baseStatusId: IStatusEnum;
  baseStatusName: string;
  maxStatusEntity: IEntityWithStatus;
  maxStatusId: IStatusEnum;
  isReadOnly: boolean;
  isActiveReadOnly: boolean;
  isActive: boolean;
  //isOkToAddEdit: boolean,
  shortDisplay: string;
  longDisplay: string;
  statusDisplay: string;
}

// Status Report interfaces

export interface ISubjectStatusIdentifier {
  label: string;
  description: string;
}

export interface IAvailableStatusDto {
  id: string;
  entityType: string;
  entityName: string;
  // activeStatusId: ActiveStatusEnum;
  // activeStatusName: string;
  statusResult: IStatusResult;
  link: string;
}

export interface IHasActiveStatus {
  activeStatusId: number;
  activeStatus: ActiveStatus;
  entityAspect: EntityAspect;
}

export interface IChangeActiveStatusParent {
  dbQueryService: DbQueryService;
  dbSaveService: DbSaveService;
}

@Injectable({
  providedIn: 'root',
})
export class StatusService {
  ref: DynamicDialogRef | undefined;
  constructor(
    private dialogService: UtilDialogService,
    private dynamicService: DialogService,
    private pngDialogService: DialogService,
    private toastr: ToastrService,
  ) {}

  // TODO: discuss this approach with Jeff - untested.
  public getWorkingStatus(entity: IEntityWithStatus, isApiEnabled = false): IStatusResult {
    const maxStatusEntity = this.findMaxStatusEntity(entity, entity);
    const maxStatusId = maxStatusEntity.getStatusId();
    const maxStatusName = IStatusEnum[maxStatusId];
    const maxStatusEntityTypeName = maxStatusEntity.entityTypeName;
    const baseStatusName = IStatusEnum[entity.getStatusId()];
    const shortDisplay =
      entity == maxStatusEntity || maxStatusEntity.getStatusId() <= IStatusEnum.Active
        ? baseStatusName
        : `${maxStatusName} is set by ${maxStatusEntityTypeName}`;
    const longDisplay =
      entity == maxStatusEntity || maxStatusEntity.getStatusId() <= IStatusEnum.Active
        ? baseStatusName
        : `${maxStatusName} is set by ${maxStatusEntityTypeName} (${baseStatusName})`;
    const isReadOnly = maxStatusId > 2 || isApiEnabled;
    const isActiveReadOnly = maxStatusId == 1 || maxStatusId > 2 || isApiEnabled;
    const isActive = maxStatusId == IStatusEnum.Active || maxStatusId == IStatusEnum.Phaseout;
    const statusDisplay = this.getStatusDisplay(
      entity,
      maxStatusEntity,
      baseStatusName,
      maxStatusName,
      maxStatusEntityTypeName,
      isApiEnabled,
    );

    return {
      baseEntity: entity,
      baseStatusId: entity.getStatusId(),
      baseStatusName: baseStatusName,
      maxStatusId: maxStatusId,
      maxStatusEntity: maxStatusEntity,
      shortDisplay: shortDisplay,
      longDisplay: longDisplay,
      statusDisplay: statusDisplay,
      isReadOnly: isReadOnly,
      isActiveReadOnly: isActiveReadOnly,
      isActive: isActive,
      //isOkToAddEdit: isOkToAddEdit
    };
  }

  async onChangeActiveStatus(
    parent: IChangeActiveStatusParent,
    row: IHasActiveStatus & BaseEntity,
    name: string,
    valFn?: (selectedStatusId: ActiveStatusEnum, errs: string[]) => Promise<boolean>,
    isAvailableFn?: (s: IStatus) => boolean,
    canSetActiveStatus?: boolean,
  ) {
    canSetActiveStatus = canSetActiveStatus ?? true;
    // TODO: PhaseOut logic not yet implemented — hide PhaseOut from status picker until it is.
    if (isAvailableFn == null) isAvailableFn = (s) => s.id !== ActiveStatusEnum.Phaseout;
    const activeStatuses = await parent.dbQueryService.getAll(ActiveStatus);
    // because DbQueryService doesn't have hasChanges on it - because its almost never needed there.
    if (parent.dbQueryService.uow.manager.hasChanges()) {
      this.toastr.warning('Save or undo all changes before changing status', 'Unable to Proceed');
      return;
    }

    const errs: string[] = [];
    const selectedStatusId = await StatusChangeDialog.open(
      this.pngDialogService,
      {
        statuses: activeStatuses,
        isAvailable: isAvailableFn,
        currentStatus: row.activeStatus,
      },
      {
        header: name + ' Status',
      },
    );

    if (selectedStatusId == row.activeStatusId || selectedStatusId == null) {
      return;
    }

    // Either provide a validation function or have this function do basic validation.

    if (valFn) {
      // You can exist early by returning false from the valFn - but normally you just add errors to errs array.
      if (!(await valFn(selectedStatusId, errs))) return;
    } else {
      if (!row.entityAspect.validateEntity()) {
        row.entityAspect.getValidationErrors().forEach((ve) => {
          errs.push(ve.errorMessage);
        });
        errs.push(' ');
        errs.push(`${name} failed validation test.  Fix ${name} before changing status.`);
      }
    }

    if (errs.length > 0) {
      await this.dialogService.statusErrorDialog(errs);
      return;
    }

    const ok = await this.dialogService.verifyProceedDialog(name + ' Status Change', this.getStatusExplanation(selectedStatusId, name));
    if (ok) {
      // some callers may want to just know the selected status BUT not have this function set it and save.
      if (canSetActiveStatus) {
        row.activeStatusId = selectedStatusId;
        await parent.dbSaveService.saveSelectedChanges([row]);
        this.toastr.success('Status change updated', 'Database Activity');
      }
      return selectedStatusId;
    } else {
      return;
    }
  }

  /**
   * The row's status as the Available? icon judges it, for a Status column: the worst status of
   * every part (Inactive first, then Hold, then Phaseout), with a Hold on the Account, Supplier or
   * Program left out. Same rule as getStatusIcon.
   */
  getAvailableStatusName(entity: ProductRelatedEntity): string {
    if (entity == null || entity.entityAspect.entityState.isDeleted() || entity.entityAspect.entityState.isDetached()) return '';
    const statusId = this.getAvailableStatusId(entity);
    return statusId == null ? '' : IStatusEnum[statusId];
  }

  /** The text class for getAvailableStatusName: red Inactive, amber Hold, dark yellow Phaseout. */
  getAvailableStatusClass(entity: ProductRelatedEntity): string {
    if (entity == null || entity.entityAspect.entityState.isDeleted() || entity.entityAspect.entityState.isDetached()) return '';
    switch (this.getAvailableStatusId(entity)) {
      case IStatusEnum.Inactive:
        return 'qb-status-inactive';
      case IStatusEnum.Hold:
        return 'qb-status-hold';
      case IStatusEnum.Phaseout:
        return 'qb-status-phaseout';
      default:
        return '';
    }
  }

  getStatusIcon(entity: ProductRelatedEntity): string {
    if (entity.entityAspect.entityState.isDeleted() || entity.entityAspect.entityState.isDetached()) {
      return '';
    }
    const statusId = this.getAvailableStatusId(entity);

    if (statusId === IStatusEnum.Active) {
      return <string>'fa-sharp fa-solid fa-check green_pointer';
    }

    // Dark yellow X: Phaseout cannot be newly ordered on Proximity (see getStatusExplanation), but the
    // supplier may still finish orders already placed - so not the red X of Inactive. Any Inactive
    // part (style, product, choice, feature, configuration) wins over Phaseout: StatusPriority.
    if (statusId === IStatusEnum.Phaseout) {
      return <string>'fa-solid fa-x yellow_pointer';
    }

    if (statusId === IStatusEnum.Inactive) {
      return <string>'fa-solid fa-x red_pointer';
    }

    if (statusId === IStatusEnum.Hold) {
      return <string>'fa-solid fa-x green_pointer';
    }
    return '';
  }

  /**
   * The "Available?" icon ignores a Hold on the Account, Supplier or Program.
   * Those three are on Hold while an admin edits, and a Hold on any of them reaches every row through
   * getStatusParents - so every icon showed X and told the admin nothing about the row itself.
   * Phaseout and Inactive on them still count: a Phaseout Supplier shows every row as a dark yellow X, and an
   * Inactive one makes every row unavailable.
   * The Status Detail dialog (getReportStatusResult) still reports their true status.
   */
  getAvailableStatusId(entity: ProductRelatedEntity): IStatusEnum | undefined {
    const statusDtos = this.createStatusReporterDtos(entity);
    if (statusDtos.length === 0) return undefined;
    let maxStatusId = IStatusEnum.Active;
    statusDtos.forEach((dto) => (maxStatusId = this.findMaxAvailableStatusId(dto.statusResult.baseEntity, maxStatusId)));
    return maxStatusId;
  }

  private findMaxAvailableStatusId(entity: IEntityWithStatus | null, maxStatusId: IStatusEnum): IStatusEnum {
    if (entity == null) return maxStatusId;
    let statusId: IStatusEnum = entity.getStatusId();
    const isContainer = entity instanceof Account || entity instanceof Supplier || entity instanceof Program;
    if (isContainer && statusId === IStatusEnum.Hold) statusId = IStatusEnum.Active;
    if (StatusPriority.indexOf(statusId) > StatusPriority.indexOf(maxStatusId)) maxStatusId = statusId;
    entity.getStatusParents().forEach((p) => (maxStatusId = this.findMaxAvailableStatusId(p, maxStatusId)));
    return maxStatusId;
  }

  getStatusExplanation(statusEnum: ActiveStatusEnum, type: string): string {
    let s = '';
    if (statusEnum == ActiveStatusEnum.Active) {
      s = `This ${type} will be activated - pending validation.  All transactions associated with this ${type} will be processed normally.`;
    }
    if (statusEnum == ActiveStatusEnum.Hold) {
      s = `This ${type} will be put on temporary hold.  All transactions associated with this ${type} will stop; new orders or returns will not be accepted.`;
    }
    if (statusEnum == ActiveStatusEnum.Phaseout) {
      s = `This ${type} will be phased out.  Any new order or return request associated with this ${type} will not be submitted by Proximity.  Suppliers may continue processing orders and returns associated with this ${type}.`;
    }
    if (statusEnum == ActiveStatusEnum.Inactive) {
      s = `This ${type} will be deactivated.  Any new order or return request associated with this ${type} will not be submitted by Proximity.  Suppliers will be notified to stop work-in-process orders and returns associated with this ${type}.  You may be subject to charges for in-process orders and returns that are stopped as a result of this status change.`;
    }
    if (s.length == 0) {
      return '';
    }
    return s;
  }

  // conceptually private except for use by the available-status-dialog.component.ts

  getReportStatusResult(entity: ProductRelatedEntity, program: Program | undefined = undefined) {
    const statusDtos = this.createStatusReporterDtos(entity, program);
    return this.getMaxReportStatusResult(statusDtos);
  }

  createStatusReporterDtos(entity: ProductRelatedEntity, program: Program | undefined = undefined) {
    let pptc: ProgramProductTypeConfig | null = null;
    let ppt: PricedProductType | null = null;
    let pt: ProductType | undefined = undefined;
    let ptc: ProductTypeConfig | null = null;
    let ptcp: ProductTypeConfigProduct | null = null;
    let p: Product | null = null;
    let s: Supplier | null = null;
    let pro: Program | null = null;

    if (program) pro = program;

    if (entity.entityAspect.entityState.isDeleted() || entity.entityAspect.entityState.isDetached()) {
      return [];
    }

    if (entity instanceof ProgramProductTypeConfig) {
      pptc = entity;
      ptc = pptc.productTypeConfig;
      if (ptc == null) {
        return [];
      }
      ppt = ptc.pricedProductType;

      pt = ppt.productType;
      pro = pptc.program;
    }

    if (entity instanceof PricedProductType) {
      ppt = entity;
      pt = ppt.productType;
    }
    if (entity instanceof ProductType) {
      pt = entity;
    }
    if (entity instanceof ProductTypeConfigProduct) {
      p = entity.product;
      pt = p.productType;
    }
    if (entity instanceof Product) {
      p = entity;
      pt = p.productType;
    }
    if (entity instanceof ProductTypeConfigProduct) {
      ptcp = entity;
      ptc = ptcp.productTypeConfig;
      ppt = ptc.pricedProductType;
      p = ptcp.product;
      pt = p.productType;
    }

    if (!pt) {
      return [];
    }

    s = pt.supplier;

    const statusDtos: IAvailableStatusDto[] = [];
    if (ppt) {
      statusDtos.push(this.createStatusDto('Account', ppt.account.name, this.getWorkingStatus(ppt.account, s.isApiEnabled), ''));
    }

    statusDtos.push(this.createStatusDto('Supplier', s.name, this.getWorkingStatus(s, s.isApiEnabled), ''));

    if (pro) {
      statusDtos.push(this.createStatusDto('Program', pro.name, this.getWorkingStatus(pro, s.isApiEnabled), ''));
    }

    if (pptc) {
      statusDtos.push(
        this.createStatusDto(
          'Product Configuration',
          pptc.productTypeConfig.name,
          this.getWorkingStatus(pptc.productTypeConfig, s.isApiEnabled),
          '',
        ),
      );
    }
    statusDtos.push(this.createStatusDto('Manufacturer', pt.manufacturer.name, this.getWorkingStatus(pt.manufacturer, s.isApiEnabled), ''));
    statusDtos.push(this.createStatusDto('Product Type', <string>pt.name, this.getWorkingStatus(pt, s.isApiEnabled), ''));
    if (ppt) {
      statusDtos.push(this.createStatusDto('Priced Product Type', <string>pt.name, this.getWorkingStatus(ppt, s.isApiEnabled), ''));
    }

    if (pt.productTypeFeatures.length > 0) {
      pt.productTypeFeatures.forEach((ptf) => {
        statusDtos.push(
          this.createStatusDto(
            'Feature',
            ptf.feature.displayName + ' (' + ptf.feature.name + ')',
            this.getWorkingStatus(ptf.feature, s.isApiEnabled),
            '',
          ),
        );
        if (p) {
          p.productFeatureChoices.forEach((pfc) => {
            if (pfc.featureChoice.featureId === ptf.feature.id) {
              statusDtos.push(
                this.createStatusDto(
                  '- - Feature Choice',
                  pfc.featureChoice.validValue,
                  this.getWorkingStatus(pfc.featureChoice, s.isApiEnabled),
                  '',
                ),
              );
            }
          });
          statusDtos.push(
            this.createStatusDto('Product', p.productType.name, this.getWorkingStatus(p as IEntityWithStatus, s.isApiEnabled), ''),
          );
        }
      });

      if (ptcp) {
        statusDtos.push(
          this.createStatusDto(
            'Configuration',
            ptcp.productTypeConfig.name,
            this.getWorkingStatus(ptcp.productTypeConfig, s.isApiEnabled),
            '',
          ),
        );

        statusDtos.push(
          this.createStatusDto(
            'Configured Product',
            <string>ptcp.product.productType.name,
            this.getWorkingStatus(ptcp, s.isApiEnabled),
            '',
          ),
        );
      }
    }
    return statusDtos;
  }

  getSubjectIdentifiers(entity: ProductRelatedEntity | null): ISubjectStatusIdentifier[] {
    let pptc: ProgramProductTypeConfig | null = null;
    let ppt: PricedProductType | null = null;
    let pt: ProductType | undefined = undefined;
    let p: Product | null = null;
    let ptc: ProductTypeConfig | null;

    if (entity instanceof ProgramProductTypeConfig) {
      pptc = entity;
      ptc = entity.productTypeConfig;
      ppt = ptc.pricedProductType;
      pt = ppt.productType;
    }

    if (entity instanceof PricedProductType) {
      ppt = entity;
      pt = ppt.productType;
    }

    if (entity instanceof ProductType) {
      pt = entity;
    }

    if (entity instanceof ProductTypeConfigProduct) {
      p = entity.product;
      pt = p.productType;
      ptc = entity.productTypeConfig;
      ppt = ptc.pricedProductType;
    }

    if (entity instanceof Product) {
      p = entity;
      pt = p.productType;
    }

    const identifiers: ISubjectStatusIdentifier[] = [];
    if (ppt) {
      identifiers.push({ label: 'Account', description: (ppt as PricedProductType).account.name });
    }

    if (pt) {
      identifiers.push({ label: 'Supplier', description: pt.supplier.name });
      identifiers.push({ label: 'Manufacturer', description: pt.manufacturer.name });
      // identifiers.push({ label: 'SKU', description: <string>pt.productKey });
      identifiers.push({ label: 'Name', description: pt.name });
    }

    if (p) {
      identifiers.push({ label: 'Feature Choice(s)', description: <string>p.getAllFeatureChoices() });
    }
    return identifiers;
  }

  // fully private methods

  private getStatusDisplay(
    entity: IEntityWithStatus,
    maxStatusEntity: IEntityWithStatus,
    baseStatusName: string,
    maxStatusName: string,
    maxStatusEntityTypeName: string,
    isApiEnabled: boolean,
  ) {
    const lDisplay =
      entity == maxStatusEntity || maxStatusEntity.getStatusId() <= IStatusEnum.Active
        ? baseStatusName
        : `${maxStatusName} is set by ${maxStatusEntityTypeName} (${baseStatusName})`;

    if (isApiEnabled) {
      return 'API-enabled';
    }

    if (maxStatusEntity.getStatusId() == ActiveStatusEnum.Active) {
      return '';
    }

    if (maxStatusEntity.getStatusId() == ActiveStatusEnum.Hold) {
      return lDisplay + ' - editable';
    }

    return lDisplay + ' - read-only';
  }

  private findMaxStatusEntity(entity: IEntityWithStatus, maxStatusEntity: IEntityWithStatus) {
    const statusId = entity.getStatusId();
    if (statusId == IStatusEnum.Inactive) return entity;
    // Use indexOf to get actual priority index (StatusPriority array is ordered by priority, not by enum value)
    const entityPriority = StatusPriority.indexOf(statusId);
    const maxPriority = StatusPriority.indexOf(maxStatusEntity.getStatusId());
    maxStatusEntity = entityPriority >= maxPriority ? entity : maxStatusEntity;

    entity.getStatusParents().forEach((e) => {
      if (e == null) return maxStatusEntity;
      maxStatusEntity = this.findMaxStatusEntity(e, maxStatusEntity);
      return maxStatusEntity;
    });

    return maxStatusEntity;
  }

  private createStatusDto(eType: string, eName: string, statusResult: IStatusResult, l: string) {
    return {
      id: Guid.create().toString(),
      entityType: eType,
      entityName: eName,
      statusResult: statusResult,
      link: l,
    };
  }

  private getMaxReportStatusResult(dtos: IAvailableStatusDto[]): IStatusResult {
    let maxResult = dtos[0].statusResult;
    dtos.forEach((dto) => {
      if (dto.statusResult.maxStatusId > maxResult.maxStatusId) {
        maxResult = dto.statusResult;
      }
    });
    return maxResult;
  }
}
